import { loadState, replaceLocalState, saveCurrentStateToAccount } from "./storage";

const PRE_LOGIN_BACKUP_KEY = "safeplate-pre-login-local-backup";

async function request(path, options = {}) {
  const response = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("Account server is not available on this preview.");
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "SafePlate could not complete that request.");
  }
  return data;
}

function hasChildren(state) {
  return Array.isArray(state?.children) && state.children.length > 0;
}

function comparableState(state) {
  return JSON.stringify({
    activeChildId: state?.activeChildId || null,
    children: (state?.children || [])
      .map((child) => ({
        id: child.id,
        name: child.name,
        age: child.age,
        allergens: child.allergens || [],
        hasEpiPen: !!child.hasEpiPen,
        epiPenLocation: child.epiPenLocation || "",
        emergencyInstructions:
          child.emergencyInstructions ||
          child?.profile?.medicalInfo?.emergencyInstructions ||
          "",
        hasAsthma: !!child?.profile?.medicalInfo?.hasAsthma,
        crossContactSensitivity: !!child?.profile?.safetySettings?.crossContactSensitivity,
        emergencyContacts: child.emergencyContacts || [],
        parentNotes: child.parentNotes || "",
        updatedAt: child.updatedAt || "",
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  });
}

function sameProfileData(a, b) {
  return comparableState(a) === comparableState(b);
}

function backupLocalState(state) {
  try {
    localStorage.setItem(
      PRE_LOGIN_BACKUP_KEY,
      JSON.stringify({ backedUpAt: new Date().toISOString(), state }),
    );
  } catch {
    // Account login should not fail because local backup storage is full.
  }
}

export async function getAccount() {
  try {
    const data = await request("/api/auth/me");
    return data.user || null;
  } catch {
    return null;
  }
}

export async function registerAccount({ email, password, displayName, familyName }) {
  const data = await request("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      displayName,
      familyName,
      initialState: loadState(),
    }),
  });

  if (data.state) replaceLocalState(data.state);
  return data.user;
}

export async function loginAccount({ email, password }) {
  const localState = loadState();
  let localProfileBackedUp = false;
  const data = await request("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

  const remoteState = data.state;
  if (remoteState?.children?.length) {
    if (hasChildren(localState) && !sameProfileData(localState, remoteState)) {
      backupLocalState(localState);
      localProfileBackedUp = true;
    }
    replaceLocalState(remoteState);
  } else if (localState.children?.length) {
    replaceLocalState(localState);
    await saveCurrentStateToAccount();
  } else if (remoteState) {
    replaceLocalState(remoteState);
  }

  return { ...data.user, localProfileBackedUp };
}

export async function logoutAccount() {
  await request("/api/auth/logout", { method: "POST", body: "{}" });
}
